import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { buildMatchers } from './highlight';

// Loads the reference data the preview overlays need: glossary terms (for the
// project's glossary), the workspace brand voice, and per-segment comment counts
// (one bulk request for the whole project group). All loads fail soft — a 403 on
// brand voice (non-owner) or a missing glossary just disables that overlay.
export default function useReferenceData({ projectId, glossaryId, token }) {
    const [glossaryTerms, setGlossaryTerms] = useState([]);
    const [brandVoice, setBrandVoice] = useState(null);
    const [commentCounts, setCommentCounts] = useState({}); // sectionIndex -> count

    const authHeader = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token],
    );

    // Glossary terms (only when the project has a glossary).
    useEffect(() => {
        if (!token || !glossaryId) { setGlossaryTerms([]); return; }
        axios.get(`/api/glossary/${glossaryId}/terms`, authHeader)
            .then((r) => setGlossaryTerms(Array.isArray(r.data) ? r.data : []))
            .catch(() => setGlossaryTerms([]));
    }, [glossaryId, token, authHeader]);

    // Brand voice (workspace-scoped; owner-gated — fail soft).
    useEffect(() => {
        if (!token) return;
        axios.get('/api/brand-voice', authHeader)
            .then((r) => setBrandVoice(r.data || null))
            .catch(() => setBrandVoice(null));
    }, [token, authHeader]);

    // Bulk comment counts for this language's segments.
    useEffect(() => {
        if (!token || !projectId) { setCommentCounts({}); return; }
        axios.get(`/api/comments/projects/${projectId}/comments`, authHeader)
            .then((r) => {
                const counts = {};
                const pid = String(projectId);
                for (const c of Array.isArray(r.data) ? r.data : []) {
                    if (c.deleted) continue;
                    // The bulk endpoint spans the whole group; keep only this language.
                    if (c.projectId && String(c.projectId) !== pid) continue;
                    counts[c.sectionIndex] = (counts[c.sectionIndex] || 0) + 1;
                }
                setCommentCounts(counts);
            })
            .catch(() => setCommentCounts({}));
    }, [projectId, token, authHeader]);

    const matchers = useMemo(
        () => buildMatchers({ glossaryTerms, brandVoice }),
        [glossaryTerms, brandVoice],
    );

    // Fetch a single segment's thread on demand (read-only display).
    const getThread = useCallback(async (sectionIndex) => {
        if (!projectId) return [];
        try {
            const r = await axios.get(
                `/api/comments/projects/${projectId}/segments/${sectionIndex}/comments`,
                authHeader,
            );
            return Array.isArray(r.data) ? r.data.filter((c) => !c.deleted) : [];
        } catch {
            return [];
        }
    }, [projectId, authHeader]);

    return { matchers, commentCounts, getThread, hasGlossary: glossaryTerms.length > 0, hasBrandVoice: !!brandVoice };
}
